/**
 * Subscribe without leaving the page.
 *
 * Shopify's customer form is a plain POST: the browser navigates, comes back on
 * ?form_type=customer, and the theme pops a generic dialog to say what
 * happened. That throws away whatever the shopper was looking at, and the
 * dialog is the theme's, not the shop's.
 *
 * The obvious fix -- cancel the submit and repost with fetch -- does not work
 * here. Bot protection is on, and Shopify answers an AJAX post to /contact with
 * 400 "Missing CAPTCHA token": the token is attached by Shopify's own code
 * hanging off the form's real submit, which cancelling skips straight past.
 *
 * So the submit is left completely alone and only its target is changed, to a
 * hidden iframe. The browser still performs a genuine form navigation -- same
 * headers, same cookies, same chance for Shopify to do its captcha work -- but
 * it lands in the frame instead of the window. The frame is same-origin, so the
 * page Shopify returns can be read for the outcome using the same markers the
 * theme itself uses: .alert--success, .alert--error.
 *
 * Two catches, and the reason for the care below.
 *
 * The first is that the shopper's click is often not a submit at all. Shopify
 * binds its captcha to the form on the first focusin, asynchronously, and if
 * the click beats the binding its own handler cancels the event and posts
 * nothing, waiting to be submitted again. Waiting for a reply to that is
 * waiting for something nobody asked for -- it was thirty seconds of a dead
 * button ending in the page reloading anyway. willBeSwallowed spots that pass
 * and sends the form again once the binding lands, which is the second click
 * the shopper would otherwise have had to make.
 *
 * The second is that Shopify does not always answer in a way that can be read.
 * Sometimes it serves a full-page hCaptcha challenge -- "let us know you're not
 * a robot" -- which is no use inside a frame nobody can see; and the reply
 * comes back on whichever domain is canonical, so on the myshopify domain it is
 * another origin and the frame is closed to us. So success is only ever claimed
 * on a success marker actually being there. Anything else -- a challenge, an
 * unreadable frame, no answer in time -- hands the submit back to the browser
 * to do the old way, which reloads the page but puts the challenge somewhere it
 * can be answered. Losing a sign-up quietly is far worse than a page reload.
 *
 * What replaces the form depends on where it is:
 *
 *   in the popup   the whole panel is swapped for a confirmation, since the
 *                  offer that was being made is now spent
 *   anywhere else  the field is swapped for a line of thanks, leaving the rest
 *                  of the footer where it was
 *
 * Errors keep the theme's existing dialog: they are rare, and a shopper who
 * typed a bad address needs to be told plainly.
 */

const FRAME_NAME = 'newsletter-inline-frame';
// A reply that has genuinely been asked for. Nothing here waits on the shopper,
// so this only has to cover a slow answer, not a captcha being filled in.
const TIMEOUT = 15000;
// How long to let Shopify attach its captcha before giving up on it.
const BIND_TIMEOUT = 8000;
// Two goes at the same address is the most this can ever need: one swallowed to
// bind the captcha, one that posts. A third means something else is wrong.
const MAX_ATTEMPTS = 2;

/** @param {Element | null} el @param {boolean} on */
const toggle = (el, on) => el && el.classList.toggle('hidden', !on);

/**
 * Whether the reply to a form post could be read at all.
 *
 * Shopify answers on the shop's canonical origin whatever host the page was
 * served from, so a shopper on www against a bare domain -- or on the myshopify
 * one -- gets a reply from somewhere the page is not allowed to look into. The
 * frame then holds a perfectly good confirmation that cannot be read, which is
 * indistinguishable from a failure and has to be treated as one.
 *
 * Checked once, up front. Trying anyway would mean the shopper watching a
 * spinner for the length of a round trip before the reload they were always
 * going to get.
 */
const CANONICAL_ORIGIN = (() => {
  const raw = document.currentScript?.dataset.canonicalOrigin;
  try {
    return raw ? new URL(raw).origin : null;
  } catch {
    return null;
  }
})();
const CAN_READ_REPLY = !CANONICAL_ORIGIN || CANONICAL_ORIGIN === window.location.origin;

/** The frame every newsletter form posts into. Made once, reused after. */
function getFrame() {
  const existing = document.querySelector(`iframe[name="${FRAME_NAME}"]`);
  if (existing) return /** @type {HTMLIFrameElement} */ (existing);

  const frame = document.createElement('iframe');
  frame.name = FRAME_NAME;
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.style.cssText =
    'position:absolute;left:-9999px;top:0;width:1px;height:1px;border:0;opacity:0;';
  document.body.appendChild(frame);
  return frame;
}

/**
 * Read the outcome out of the page Shopify sent back. Anything that is not
 * plainly one thing or the other is 'unknown', which is handed back to the
 * browser rather than guessed at.
 * @param {Document | null} doc
 * @returns {'success'|'error'|'unknown'}
 */
function outcome(doc) {
  if (!doc) return 'unknown';
  // The confirmation Liquid renders on a posted_successfully? page. This is
  // the marker rather than the theme's success alert, which was removed so a
  // generic modal would stop appearing over every sign-up.
  if (doc.querySelector('[data-newsletter-inline-success]:not(.hidden)')) return 'success';
  if (doc.querySelector('.alert--success')) return 'success';
  // An error alert that is not the always-present hidden "already subscribed"
  // placeholder means the address itself was rejected.
  if (doc.querySelector('.alert--error:not(.hidden)')) return 'error';
  return 'unknown';
}

/** @param {HTMLFormElement} form */
function showSuccess(form) {
  const popup = form.closest('.subscribe-popup__content');
  const panel = popup?.querySelector('[data-newsletter-success]');
  if (popup && panel) {
    // Everything the shopper was offered is done with; leave only the thanks.
    // Set outright rather than via .hidden -- the theme's own block classes set
    // display later in the stylesheet and simply win, which left the offer
    // still sitting above the confirmation.
    for (const child of popup.children) {
      if (child !== panel) /** @type {HTMLElement} */ (child).style.display = 'none';
    }
    toggle(panel, true);
    panel.setAttribute('tabindex', '-1');
    /** @type {HTMLElement} */ (panel).focus({ preventScroll: true });
    return;
  }

  const wrapper = form.closest('newsletter-form') ?? form;
  // The whole field goes, input and button with it -- there is nothing left to
  // type, so leaving an empty box behind only invites a second go.
  toggle(wrapper.querySelector('.newsletter-form__field'), false);
  toggle(wrapper.querySelector('[data-newsletter-inline-success]'), true);
}

/**
 * Whether Shopify is about to swallow this submit to attach its captcha.
 *
 * Its bootstrap marks every form it protects with data-cptcha, then binds
 * hCaptcha on the first focusin. Binding is asynchronous, so a shopper who
 * types and clicks quickly gets there first: the submit handler cancels the
 * event to wait for the binding and posts nothing at all. The click after that
 * one is the one that actually goes.
 *
 * This is the difference between "the reply is on its way" and "nothing was
 * ever sent", and telling them apart is the whole point -- waiting on the
 * second is a wait that never ends.
 * @param {HTMLFormElement} form
 */
function willBeSwallowed(form) {
  return (
    form.dataset.cptcha === 'true' &&
    form.dataset.hcaptchaBound !== 'true' &&
    form.dataset.recaptchaBound !== 'true'
  );
}

/** @param {HTMLFormElement} form @param {boolean} on */
function setBusy(form, on) {
  const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('[type="submit"]'));
  if (!button) return;
  // The theme's own busy state: it hides the label and shows the spinner the
  // button already carries. Left enabled would invite a second click landing
  // mid-flight, which Shopify would treat as a fresh attempt.
  button.classList.toggle('btn--loading', on);
  button.disabled = on;
  button.setAttribute('aria-busy', String(on));
}

/** @param {HTMLFormElement} form */
function showError(form) {
  toggle(form.querySelector('[id$="__error-subscribed"]'), true);
  const dialog = /** @type {any} */ (form.querySelector('dialog-component'));
  if (dialog && typeof dialog.showDialog === 'function') dialog.showDialog();
}

/**
 * Pick the confirmation back up after a reload.
 *
 * When a submit could not be kept on the page -- a captcha challenge, mostly --
 * it goes the old way and Shopify returns to ?customer_posted=true, keeping the
 * form's id in the fragment. Liquid has already put the thanks in place of the
 * field. All that is missing is the popup, which needs opening again and
 * swapping to its own panel; otherwise someone who signed up there is dropped
 * back on the homepage with no sign anything happened.
 */
function restoreAfterReload() {
  const id = decodeURIComponent(location.hash.slice(1));
  if (!id) return;

  const form = document.getElementById(id);
  if (!(form instanceof HTMLFormElement)) return;
  // Only when Liquid actually rendered a confirmation, so a stray fragment
  // cannot conjure one up.
  if (!form.querySelector('[data-newsletter-inline-success]:not(.hidden)')) return;
  if (!form.closest('.subscribe-popup__content')) return;

  showSuccess(form);
  const host = /** @type {any} */ (document.querySelector('subscribe-popup-component'));
  if (!host) return;
  // The component may not have upgraded yet; showDialog only exists after.
  customElements.whenDefined('subscribe-popup-component').then(() => {
    if (typeof host.showDialog === 'function') host.showDialog();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', restoreAfterReload, { once: true });
} else {
  restoreAfterReload();
}

document.addEventListener(
  'submit',
  (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('newsletter-form')) return;
    // Already handed back to the browser -- let it through untouched.
    if (form.dataset.inlineFallback === 'true') return;
    // Shopify's captcha code cancels the first submit and re-issues it once it
    // has a token. That second pass is the same attempt, not a new one.
    if (form.dataset.inlinePending === 'true') return;

    // No point posting into a frame whose answer cannot be read. Let it go the
    // ordinary way -- the spinner stays up through the reload, and Liquid puts
    // the confirmation in place on the way back.
    if (!CAN_READ_REPLY) {
      setBusy(form, true);
      return;
    }

    // Deliberately not cancelled. The browser submits as it always did; only
    // where the answer lands has changed.
    const frame = getFrame();
    form.target = FRAME_NAME;
    form.dataset.inlinePending = 'true';
    setBusy(form, true);

    // The submit Shopify is about to cancel to attach its captcha. Nothing is
    // being posted, so there is no reply to wait for: watch for the binding to
    // land and send it again ourselves. This is the click the shopper would
    // otherwise have to make a second time -- and, before this, the thirty
    // seconds of nothing that ended in the page reloading.
    if (willBeSwallowed(form)) {
      const attempts = Number(form.dataset.inlineAttempts ?? 0) + 1;
      form.dataset.inlineAttempts = String(attempts);
      delete form.dataset.inlinePending;

      if (attempts > MAX_ATTEMPTS) {
        form.dataset.inlineFallback = 'true';
        form.requestSubmit();
        return;
      }

      const started = Date.now();
      const wait = setInterval(() => {
        if (!willBeSwallowed(form)) {
          clearInterval(wait);
          form.requestSubmit();
        } else if (Date.now() - started > BIND_TIMEOUT) {
          // The captcha never attached. Hand it to the browser, which at least
          // puts whatever Shopify wants to ask somewhere it can be answered.
          clearInterval(wait);
          form.dataset.inlineFallback = 'true';
          form.requestSubmit();
        }
      }, 100);
      return;
    }

    let done = false;
    const finish = (result) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      frame.removeEventListener('load', onLoad);
      form.target = '';
      delete form.dataset.inlinePending;

      if (result === 'unknown') {
        // A challenge to answer, or a reply from another origin that cannot be
        // read -- the storefront's own domain and the myshopify one are not the
        // same origin, and Shopify answers on whichever is canonical. Either
        // way, send it the ordinary way: the page reloads, but the sign-up
        // lands and anything that needs asking gets asked. This attempt
        // subscribed nobody, so nothing is duplicated. The button stays busy
        // through the reload rather than flicking back to idle first.
        form.dataset.inlineFallback = 'true';
        form.requestSubmit();
        return;
      }

      setBusy(form, false);
      try {
        if (result === 'error') showError(form);
        else showSuccess(form);
      } catch (e) {
        console.error('newsletter-inline:', e);
      }
    };

    const onLoad = () => {
      let doc = null;
      try {
        // about:blank is the frame settling, not Shopify answering.
        const href = frame.contentWindow?.location.href;
        if (!href || href === 'about:blank') return;
        doc = frame.contentDocument;
      } catch {
        // Unreadable. Treated as unknown, not as success.
      }
      finish(outcome(doc));
    };

    const timer = setTimeout(() => finish('unknown'), TIMEOUT);
    frame.addEventListener('load', onLoad);
  },
  // Capture, so the target is set before anything else looks at the form.
  true
);
